interface MandalaBackgroundProps {
  /** Tailwind size class for the mandala (e.g. "w-[800px] h-[800px]"). */
  size?: string;
  /** Vertical positioning class for the mandala layer. */
  position?: string;
  /** Opacity (0-1) of the mandala. */
  opacity?: number;
  /** Whether to render the radial light glow at top-center. */
  withRadialLight?: boolean;
  /** Whether to include the grain overlay. */
  withGrain?: boolean;
}

/**
 * Atmospheric backdrop layer:
 * - faint mandala SVG (sacred geometry, low opacity)
 * - subtle SVG grain overlay
 * - optional top-center radial light wash
 *
 * Pure decoration. Always pointer-events-none.
 */
export default function MandalaBackground({
  size = "w-[min(90vw,800px)] h-[min(90vw,800px)]",
  position = "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
  opacity = 0.05,
  withRadialLight = true,
  withGrain = true,
}: MandalaBackgroundProps) {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden select-none" aria-hidden>
      {withRadialLight && (
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 60% 50% at 50% 0%, hsl(var(--marigold) / 0.08), transparent 70%)",
          }}
        />
      )}

      <div className={`absolute ${position} ${size}`} style={{ opacity }}>
        <svg viewBox="0 0 600 600" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full text-manuscript">
          {[280, 220, 160, 100, 40].map((r) => (
            <circle key={r} cx="300" cy="300" r={r} stroke="currentColor" strokeWidth="0.5" />
          ))}
          {Array.from({ length: 12 }).map((_, i) => {
            const angle = (i * 30 * Math.PI) / 180;
            return (
              <line
                key={i}
                x1="300"
                y1="300"
                x2={300 + 280 * Math.cos(angle)}
                y2={300 + 280 * Math.sin(angle)}
                stroke="currentColor"
                strokeWidth="0.4"
              />
            );
          })}
          {Array.from({ length: 8 }).map((_, i) => {
            const angle = ((i * 45 + 22.5) * Math.PI) / 180;
            return (
              <circle
                key={`p${i}`}
                cx={300 + 190 * Math.cos(angle)}
                cy={300 + 190 * Math.sin(angle)}
                r="20"
                stroke="currentColor"
                strokeWidth="0.4"
                fill="none"
              />
            );
          })}
        </svg>
      </div>

      {withGrain && (
        <div
          className="absolute inset-0 mix-blend-overlay"
          style={{
            opacity: 0.06,
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.6 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
          }}
        />
      )}
    </div>
  );
}
